type DateFormatMode = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | "date-only";
import { getEnvConfig } from "~/services/appConfig";

type DateInput = Date | string | null | undefined;
/**
 * 0 - date only (no DOW) - 2022-01-01
 * 1 - date and time with DOW - 2022-01-01 Sat 1:50 PM
 * 2 - time only with DOW - Sat 12:00 PM
 * 3 - time only (no DOW) - 12:00 PM
 * 4 - date with DOW - 2022-01-01 Sat
 * 5 - date and time with DOW and seconds - 2022-01-01 Sat 12:00:00 PM
 * 6 - date with DOW and UTC time - 2022-01-01 Sat 06:00:00
 * 7 - date spelled out - 2025 Apr 30 (Wed) 10:25 AM
 * 8 - month and year - Apr 2025
 * 9 - long date - December 1, 2022
 */
function formatDate(date: DateInput, mode: DateFormatMode = 0): string {
  if (!date) {
    return "";
  }
  const newDate = new Date(date ?? "");

  if (isNaN(newDate.getTime())) {
    // Check if the date is invalid
    return "-";
  } else {
    const day = newDate.toLocaleDateString("en-US", { day: "2-digit" });
    const month = newDate.toLocaleDateString("en-US", { month: "2-digit" });
    const year = newDate.toLocaleDateString("en-US", { year: "numeric" });

    if (mode === 0) {
      return `${year}-${month}-${day}`;
    }

    const time = newDate.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "numeric",
      hourCycle: "h12",
    });

    if (mode === 3) {
      return time;
    }

    const weekday = newDate.toLocaleDateString("en-US", { weekday: "short" });

    if (mode === 2) {
      return `${weekday} ${time}`;
    }

    if (mode === 1) {
      return `${year}-${month}-${day} ${weekday} ${time}`;
    }

    if (mode === 4) {
      return `${year}-${month}-${day} ${weekday}`;
    }
    if (mode === 5) {
      const timeWithSeconds = newDate.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "numeric",
        second: "numeric",
        hourCycle: "h12",
      });
      return `${year}-${month}-${day} ${weekday} ${timeWithSeconds}`;
    }
    if (mode === 6) {
      const hours = String(newDate.getUTCHours()).padStart(2, "0");
      const minutes = String(newDate.getUTCMinutes()).padStart(2, "0");
      const seconds = String(newDate.getUTCSeconds()).padStart(2, "0");

      const timeWithSeconds = `${hours}:${minutes}:${seconds}`;
      return `${year}-${month}-${day} ${weekday} ${timeWithSeconds}`;
    }

    if (mode === 7) {
      const monthName = newDate.toLocaleDateString("en-US", {
        month: "short",
      });
      const weekdayName = newDate.toLocaleDateString("en-US", {
        weekday: "short",
      });
      return `${year} ${monthName} ${day} (${weekdayName}) ${time}`;
    }

    if (mode === 8) {
      return newDate.toLocaleDateString("en-US", {
        month: "short",
        year: "numeric",
      });
    }

    if (mode === 9) {
      return newDate.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      });
    }

    if (mode === "date-only") {
      return `${newDate.toLocaleDateString("en-US", {
        month: "long",
      })} ${day}, ${year}`;
    }

    return "";
  }
}

// define overload signatures and function
function dateOnly(d: Date | null | undefined): string;
function dateOnly(d: string | null | undefined): string;
function dateOnly(d: DateInput): string;

function dateOnly(d: DateInput): string {
  if (typeof d === "string") {
    // 2023-09-11
    if (d.length === 10) return d;

    // 2023-09-11T17:09:18
    if (d.length === 19 && d[10] === "T") {
      return d.substring(0, 10);
    }
    return dateOnly(new Date(d));
  }
  if (!d) return "";
  return toLocalISODateString(d);
}

function toLocalISODateString(date: DateInput) {
  if (!date) return "";
  if (typeof date === "string") {
    date = new Date(date);
  }

  const pad = (num: number) => {
    return (num < 10 ? "0" : "") + num;
  };

  const yyyy = date.getFullYear();
  const mm = pad(date.getMonth() + 1); // Months are zero-based
  const dd = pad(date.getDate());

  // Construct a local ISO string for date only
  return `${yyyy}-${mm}-${dd}T00:00:00`;
}
function firstDayOfNextMonth(): Date {
  const date = new Date();
  return new Date(date.getFullYear(), date.getMonth() + 1, 1);
}

function parseDate(dateString: string | null): Date | null {
  const date = dateString ? new Date(dateString) : null;
  if (date?.toISOString() === "0001-01-01T00:00:00.000Z") {
    return null;
  }
  return date;
}

/**
 * Gets the current date for time frame evaluation, with optional override for testing.
 * In development and sunweb environments, the date can be overridden by setting
 * 'timeFrameOverrideDate' in localStorage.
 * @returns Current date or overridden date for time frame comparisons
 */
function getCurrentDateForTimeFrames(): Date {
  // Only allow override in development and sunweb environments
  const env = getEnvConfig().env;
  if (env === "development" || env === "sunweb") {
    const overrideDate = localStorage.getItem("timeFrameOverrideDate");
    if (overrideDate) {
      const parsed = new Date(overrideDate);
      // Validate that the date is valid
      if (!isNaN(parsed.getTime())) {
        return parsed;
      }
    }
  }
  return new Date();
}

const standardDateFormat = "YYYY-MMM-DD ddd";
const shortDateFormat = "YYYY-MMM-DD";

export {
  dateOnly,
  firstDayOfNextMonth,
  formatDate,
  getCurrentDateForTimeFrames,
  parseDate,
  shortDateFormat,
  standardDateFormat,
  toLocalISODateString,
};
